import { once } from "es-toolkit";
import { throttle } from "es-toolkit/compat";
import Emitter from "./emitter";
import { Adapter, RealtimeMessage } from "./adapters/types";

// This client only ever speaks the remote half of the protocol: it pings, and it
// listens for what a host says back. Nothing here plays the host — teach does, by hand,
// in `teach/src/hooks/useToolkitIframeRealtimeHost.ts` — so ENTER/PONG/DEPART are
// inbound-only topics. Exported so `testHelpers.ts`'s fake host answers the same strings
// instead of keeping a copy that can drift from these.
export const Protocol = {
  // a remote pings on entry
  PING: "protocol::ping",
  // existing hosts pong in reply
  PONG: "protocol::pong",

  // a host announces itself on enter
  ENTER: "protocol::enter",
  // and departs when it goes away
  DEPART: "protocol::depart",
} as const;

// Sealed with `as const satisfies` rather than annotated `Record<string, Role>`: the
// annotation let `CLIENT_ROLE.HOST` keep typechecking as a `Role` after the entry was
// deleted, which is exactly the mistake this step is removing.
export const CLIENT_ROLE = {
  REMOTE: "remote",
  DISPLAY: "display",
} as const satisfies Record<string, Role>;
export type Role = "remote" | "display";

const INITIAL_PING_TIMEOUT = 1000;
const THROTTLE_PING_INTERVAL = 5000;

declare global {
  interface Window {
    __TOOLKIT_DEBUG__: boolean;
  }
}

export default class RealtimeClient extends Emitter {
  _role: Role;
  _channel: string;
  _client: Adapter;
  _peers: Record<string, number>;
  _id: string;
  _hasPingedForHosts: boolean;

  constructor(role: Role, channel: string, client: Adapter, id?: string) {
    super();

    if (!(client?.publish && client.subscribe && client.unsubscribe)) {
      throw new Error("Invalid client provided");
    }

    this._role = role;
    this._channel = channel;
    this._client = client;
    this._peers = {};
    // Callers can supply a pre-existing client id so that this instance is
    // recognized as the same peer across rails (e.g. toolkit reuses its
    // NATS senderId here so the self-echo filter in `_onMessage` catches
    // its own messages arriving via the iframe-postMessage rail).
    this._id = id ?? generateClientId(role);

    // NOTE: these are only used when role is remote
    this._hasPingedForHosts = false;

    this._client.subscribe?.(channel, this._onMessage);

    // Bound at registration: the emitter calls listeners unbound, so a plain
    // `this._debug` reference would lose `this` if it ever needed the instance.
    this.on("message", (...args) => this._debug(...args));

    // NOTE: we can't be sure this will actually fire when the window unloads, which
    // is why we have the loop that evicts stale peers. Usually it seems to work when
    // the window is being closed but haven't figure out yet how to make this happen
    // when an iframe is unmounted
    window.addEventListener(
      "beforeunload",
      once(() => {
        this.close();
        // NOTE: have to explicitly return nothing so that IE doesn't display prompt
        // saying "Are you sure you want to leave this page?"
        return;
      }),
    );
  }

  _debug(...args: unknown[]) {
    if (window.__TOOLKIT_DEBUG__ == null) return;
    // eslint-disable-next-line no-console
    console.log("realtime message: ", ...args);
  }

  // all internal methods as well as external callers should publish via this
  publish(topic: string, payload = {}) {
    if (this._role === CLIENT_ROLE.REMOTE) {
      return this.hasHostConnected().then((hasHost) => {
        if (!hasHost) {
          return;
        }

        return this._actualPublish(topic, payload);
      });
    }
    return this._actualPublish(topic, payload);
  }

  // in the rare chance you need to bypass normal publish logic, you can go straight here
  _actualPublish(topic: string, payload = {}) {
    const message = {
      sender: this._id,
      topic,
      payload,
    };

    return this._client.publish?.(this._channel, message);
  }

  // Returns nothing: this used to resolve after a host's `protocol::depart` publish, and
  // the annotation is explicit so a stray `await client.close()` reads as a mistake.
  close(): void {
    this._client.unsubscribe?.(this._channel, this._onMessage);
  }

  private _checkingForHosts = throttle(() => {
    let checking = Promise.resolve<unknown>(undefined);
    if (!this._hasPingedForHosts) {
      this._hasPingedForHosts = true;
      checking = this._waitForPong();
      // we bypass normal `publish` logic because `publish` calls `hasHostConnected`
      void this._actualPublish(Protocol.PING);
    }
    return checking;
  }, THROTTLE_PING_INTERVAL);

  // Resolves on the first pong, or after INITIAL_PING_TIMEOUT so a remote that heard
  // nothing can ping again later. The timeout is deliberately not cleared on a pong:
  // clearing the ping flag is what lets a later check re-ping once the throttle lapses.
  private _waitForPong() {
    return new Promise<void>((resolve) => {
      const onPong = () => {
        this.removeListener(Protocol.PONG, onPong);
        resolve();
      };
      this.on(Protocol.PONG, onPong);

      setTimeout(() => {
        this.removeListener(Protocol.PONG, onPong);
        this._hasPingedForHosts = false;
        resolve();
      }, INITIAL_PING_TIMEOUT);
    });
  }

  async hasHostConnected() {
    if (this._role !== CLIENT_ROLE.REMOTE) {
      throw new Error("Only remotes can call hasHostConnected()");
    }

    if (Object.keys(this._peers).length > 0) {
      return true;
    }

    // es-toolkit/compat's `throttle` hands back the *last* invocation's result, so a
    // call swallowed by the throttle window can return `undefined` rather than the
    // in-flight promise. There is nothing to await then, and `_peers` is empty per the
    // check above, so answer false instead of asserting a promise that may not exist.
    // Logged because the caller is usually `publish`, which drops the message on false.
    const checkingForHosts = this._checkingForHosts();
    if (checkingForHosts === undefined) {
      this._debug("hasHostConnected: throttled host check returned no promise, reporting no host");
      return false;
    }

    return checkingForHosts.then(() => {
      return Object.keys(this._peers).length > 0;
    });
  }

  _addPeer(id: string) {
    this._peers[id] = Date.now();
  }

  _removePeer(id: string) {
    delete this._peers[id];
  }

  // NOTE: this needs to be a bound method because it is passed to a subscriber above
  _onMessage = (message: RealtimeMessage) => {
    const { sender, topic, payload } = message;

    if (sender === this._id) {
      return;
    }

    // emit specialized events for protocol messages
    // as of now protocol messages don't carry a payload, only the sender id is relevant
    if (topic?.startsWith("protocol::")) {
      // for internal use
      this.emit(topic, {
        id: sender,
      });

      // REMOTE keeps track of peers
      if (this._role === CLIENT_ROLE.REMOTE) {
        if (
          // peer responded with PONG
          topic === Protocol.PONG ||
          // host running recent code sends ENTER message
          topic === Protocol.ENTER ||
          // this is for a new remote hearing from an old host which sent a PING instead of ENTER
          (topic === Protocol.PING && typeof sender === "string" && sender.includes("host_dojo"))
        ) {
          this._addPeer(sender);
        }

        if (topic === Protocol.DEPART) {
          this._removePeer(sender);
        }
      }

      return;
    }

    this.emit("message", topic, payload);
  };
}

function generateClientId(role: Role) {
  return `${role}_dojo_realtime_${Math.random().toString(36).slice(2, 8)}`;
}
